import { FC, useState } from "react";

import { ChevronLeft, ChevronRight } from "@material-ui/icons";
import { downloadAttachment } from "components/chat/utils/downloadAttachment";
import Text from "components/Text";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Sentry } from "sentry";

import FilePreviewUnavailableView from "./FilePreviewUnavailableView";
import FilePreviewView from "./FilePreviewView";
import { FileControlWrapper } from "./FilesCache";
import { useFilesContext } from "./FilesControl";

export interface AllFilePreviewProps {
  className?: string;
}

/**
 * AllFilePreview
 * Framework component to render previews for multiple files.
 * - Iterates over `files` and renders a FilePreviewView for each entry.
 * - Calls `onClickDownload` with the file when a download is requested.
 * - Minimal markup + classNames provided for styling hooks.
 */
const AllFilePreview: FC<AllFilePreviewProps> = ({ className }) => {
  const { useGetFiles } = useFilesContext();
  const files = useGetFiles();
  const [currentFile, setCurrentFile] = useState<FileControlWrapper | undefined>(files[0]);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [_isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (!currentFile || !currentFile.file || !currentFile.file.url || !currentFile.file.url_preview) {
      const message = t("fileUploads.generic_download_error", { name: currentFile?.file?.name || "file" });
      enqueueSnackbar(message, { variant: "error" });
      return;
    }

    setIsDownloading(true);
    try {
      await downloadAttachment(currentFile.file as { url: string; name: string });
    } catch (error) {
      console.error(error);
      const message = t("fileUploads.generic_download_error", { name: currentFile.file.name });
      Sentry.captureException(message, {
        extra: {
          error,
        },
      });
      enqueueSnackbar(message, { variant: "error" });
    }
    setIsDownloading(false);
  };

  const handleChangeFile = (direction: "next" | "previous") => {
    const currentIndex = files.findIndex((f) => f.file.id === currentFile?.file.id);

    if (currentIndex === files.length - 1 && direction === "next") {
      setCurrentFile(files[0]);
    } else if (currentIndex === 0 && direction === "previous") {
      setCurrentFile(files[files.length - 1]);
    } else {
      const newIndex = direction === "next" ? currentIndex + 1 : currentIndex - 1;
      setCurrentFile(files[newIndex]);
    }
  };

  if (!currentFile) {
    return (
      <div className={`all-file-preview ${className ?? ""}`.trim()}>
        <FilePreviewUnavailableView
          file={{}}
          reason={"noFiles"}
          onClickDownload={() => {
            {
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col p-2">
      <div className="flex flex-row items-center justify-center">
        <ChevronLeft onClick={() => handleChangeFile("previous")} />
        <Text size="medium">{currentFile.file.name}</Text>
        <ChevronRight onClick={() => handleChangeFile("next")} />
      </div>
      <div className="w-full h-full flex flex-col items-center justify-center overflow-y-scroll">
        <FilePreviewView
          file={currentFile?.file}
          onClickDownload={handleDownload}
          objectProps={{ className: "w-full" }}
          csvMaxRows={500}
        />
      </div>
    </div>
  );
};

export default AllFilePreview;
